import React, { useEffect, useRef } from "react";
import { X } from "lucide-react";
import { useIntl } from "react-intl";
import "./ChartSettingsDialog.sass";

const CLOSE_ICON_SIZE = 18;
const TITLE_ID = "chart-settings-dialog-title";

/**
 * Frame of a settings modal of an indicator or a drawing (po-chart-visual-spec.md
 * §5.4, §7.4): the title with ✕, the content, Remove / Cancel / Save. The chart
 * changes only on Save; Cancel, ✕, Escape and a click outside drop the edits.
 *
 * @param {object}   props
 * @param {string}   props.title
 * @param {React.ReactNode} props.children
 * @param {() => void} props.onSave
 * @param {() => void} props.onRemove
 * @param {() => void} props.onClose
 */
const ChartSettingsDialog = ({ title, children, onSave, onRemove, onClose }) => {
  const intl = useIntl();
  const t = (id) => intl.formatMessage({ id });
  const dialogRef = useRef(null);

  // Escape reaches the modal only while the focus is inside it
  useEffect(() => {
    dialogRef.current?.focus();
  }, []);

  const handleKeyDown = (event) => {
    if (event.key !== "Escape") return;

    // A menu under the modal and the selection of a drawing stay
    event.stopPropagation();
    onClose();
  };

  const handleBackdropClick = (event) => {
    if (event.target === event.currentTarget) onClose();
  };

  return (
    <div className="chart-settings-dialog__backdrop" onClick={handleBackdropClick}>
      <div
        ref={dialogRef}
        className="chart-settings-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={TITLE_ID}
        tabIndex={-1}
        onKeyDown={handleKeyDown}
      >
        <div className="chart-settings-dialog__header">
          <h2 id={TITLE_ID} className="chart-settings-dialog__title">
            {title}
          </h2>
          <button
            type="button"
            className="chart-settings-dialog__close"
            aria-label={t("futures.chart.indicator_settings.close")}
            title={t("futures.chart.indicator_settings.close")}
            onClick={onClose}
          >
            <X size={CLOSE_ICON_SIZE} aria-hidden="true" />
          </button>
        </div>
        {children}
        <div className="chart-settings-dialog__actions">
          <button
            type="button"
            className="chart-settings-dialog__btn chart-settings-dialog__btn--remove"
            onClick={onRemove}
          >
            {t("futures.chart.indicator_settings.remove")}
          </button>
          <button
            type="button"
            className="chart-settings-dialog__btn chart-settings-dialog__btn--cancel"
            onClick={onClose}
          >
            {t("futures.chart.indicator_settings.cancel")}
          </button>
          <button
            type="button"
            className="chart-settings-dialog__btn chart-settings-dialog__btn--save"
            onClick={onSave}
          >
            {t("futures.chart.indicator_settings.save")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChartSettingsDialog;
